"use client";

import Image from "next/image";
import Link from "next/link";
import { Minus, Plus, Trash2 } from "lucide-react";
import { formatPrice } from "@/lib/utils";
import { cartLineIdentity } from "@/lib/cart/line-identity";
import type { ShopifyCartLine } from "@/types/shopify";
import { CART_LINE_QUANTITY_MAX } from "@/lib/cart/limits";

interface CartLineRowProps {
  line: ShopifyCartLine;
  disabled?: boolean;
  onRemove: (lineId: string) => void;
  onUpdateQuantity: (lineId: string, quantity: number) => void;
}

export function CartLineRow({
  line,
  disabled,
  onRemove,
  onUpdateQuantity,
}: CartLineRowProps) {
  const img = line.merchandise.product.images.nodes[0];
  const identity = cartLineIdentity(line);
  const isCustom = identity.isCustom;
  const productHref = `/products/${line.merchandise.product.handle}`;

  return (
    <div className="flex gap-4 pb-4 border-b" style={{ borderColor: "#2A2A2A" }}>
      <Link
        href={productHref}
        className="relative w-16 h-16 rounded-md flex-shrink-0 overflow-hidden"
        style={{ backgroundColor: "#1E1E1E" }}
      >
        {img && (
          <Image
            src={img.url}
            alt={img.altText ?? identity.name}
            fill
            className="object-cover"
            sizes="64px"
          />
        )}
        {isCustom && (
          <div
            className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-center"
            style={{ backgroundColor: "rgba(10,10,10,0.7)", color: "#E8C547" }}
          >
            CUSTOM
          </div>
        )}
      </Link>

      <div className="flex-1 min-w-0">
        {/* The name wraps. It used to be `truncate`, which on a phone cut
            "Ultra Flex Bass Strings Medi…" — losing the string count and the
            gauges, the two facts that tell one set from another. Those are now
            structured details, read from the verified product record. */}
        <Link href={productHref} className="block text-sm font-medium break-words hover:text-[#E8C547]">
          {identity.name}
        </Link>

        {identity.warning ? (
          <p className="text-xs mt-1" style={{ color: "#F5B5B5" }}>
            {identity.warning}
          </p>
        ) : null}

        {identity.details.length > 0 && (
          <dl className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs" style={{ color: "#9A9A9A" }}>
            {identity.details.map((detail) => (
              <div key={detail.label} className="flex gap-1">
                <dt style={{ color: "#8A8A8A" }}>{detail.label}:</dt>
                <dd className="break-words">{detail.value}</dd>
              </div>
            ))}
          </dl>
        )}

        <p className="text-sm font-semibold mt-1" style={{ color: "#E8C547" }}>
          {formatPrice(line.cost.totalAmount.amount)}
        </p>

        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-2">
          <button
            onClick={() =>
              line.quantity <= 1
                ? onRemove(line.id)
                : onUpdateQuantity(line.id, line.quantity - 1)
            }
            disabled={disabled}
            className="p-2.5 rounded transition-colors hover:bg-[#2A2A2A] disabled:opacity-40"
            style={{ color: "#9A9A9A" }}
            aria-label="Decrease quantity"
          >
            <Minus size={16} />
          </button>
          <span className="flex h-10 w-10 items-center justify-center text-sm text-center" aria-live="polite">
            {disabled ? (
              <span
                className="h-4 w-4 rounded-full border-2 border-[#3A3A3A] border-t-[#E8C547] animate-spin"
                aria-hidden
              />
            ) : (
              line.quantity
            )}
          </span>
          <button
            onClick={() => onUpdateQuantity(line.id, line.quantity + 1)}
            disabled={disabled || line.quantity >= CART_LINE_QUANTITY_MAX}
            className="p-2.5 rounded transition-colors hover:bg-[#2A2A2A] disabled:opacity-40"
            style={{ color: "#9A9A9A" }}
            aria-label="Increase quantity"
          >
            <Plus size={16} />
          </button>
          <button
            onClick={() => onRemove(line.id)}
            disabled={disabled}
            className="p-2.5 rounded transition-colors hover:bg-[#2A2A2A] disabled:opacity-40 ml-auto shrink-0"
            style={{ color: "#8A8A8A" }}
            aria-label="Remove item"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
